import { ChangeDetectionStrategy, Component, inject, OnInit, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BlockedWebViewComponent } from '@ui-components';
import {
  AppRoutes,
  AUTH_CLIENT_TOKEN,
  AuthClientProvider,
  HostedGamesRoutes,
  MODAL_TOKEN,
  ModalProvider,
  PLATFORM_TOKEN,
  PlatformProvider,
  PresentModalComponent
} from '@core';
import { ActivatedRoute, ParamMap, Params, Router } from '@angular/router';
import { combineLatest, filter, map, Observable, switchMap, take, tap } from 'rxjs';
import { ReassingGameCommandHandler } from '../../application/handlers';

@Component({
  selector: 'lib-reassign-game',
  templateUrl: './reassign-game.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [BlockedWebViewComponent],
  providers: [ReassingGameCommandHandler]
})
export class ReassignGameComponent implements OnInit {
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly platformProvider: PlatformProvider = inject(PLATFORM_TOKEN);
  private readonly authClientProvider: AuthClientProvider = inject(AUTH_CLIENT_TOKEN);
  private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly router: Router = inject(Router);
  private readonly reassingGameCommandHandler: ReassingGameCommandHandler = inject(ReassingGameCommandHandler);

  readonly isNative: boolean = this.platformProvider.isNativeRun;

  readonly redirectPath: Signal<string | undefined> = toSignal(
    combineLatest([this.activatedRoute.params, this.activatedRoute.queryParamMap]).pipe(
      map(
        ([params, paramMap]: [Params, ParamMap]) =>
          `${AppRoutes.HOST}/${HostedGamesRoutes.REASSIGN}/${params['gameId']}?token=${paramMap.get('token')}`
      )
    )
  );

  ngOnInit(): void {
    if (!this.isNative) return;

    this.authClientProvider.isAuthed$
      .pipe(
        take(1),
        tap((isAuthed: boolean) => {
          if (!isAuthed) {
            this.router.navigateByUrl(AppRoutes.AUTH);
          }
        }),
        filter((isAuthed: boolean) => isAuthed),
        switchMap(() => this.activatedRoute.params),
        take(1),
        switchMap((params: Params) => {
          const rawGameId: string = params['gameId'];
          const [gameId, queryString] = rawGameId.split('?');
          const token: string = queryString ? (new URLSearchParams(queryString).get('token') ?? '') : '';

          return this.reassingGameCommandHandler.reassign(gameId, token);
        }),

        switchMap(() => this.router.navigateByUrl(AppRoutes.DASHBOARD))
      )
      .subscribe(() => this.showSuccessMessage());
  }

  private showSuccessMessage(): Observable<void> {
    return this.modalProvider.showModal$({
      component: PresentModalComponent,
      cssClass: 'present-modal',
      componentProps: {
        header: 'Gierka została przepisana!',
        message: '',
        btnTxt: 'Rozumiem, dzięki!'
      }
    });
  }
}
